import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Shared shell for every wizard step (Test Data / Select Format / Configure
 * Parameters / Configure Groups) — bounds the step to a fixed-height panel
 * instead of letting each step's own content height push the page taller.
 * The body is the only part that scrolls (`flex-1 min-h-0 overflow-y-auto`);
 * the Back/Next footer is a flex sibling below it, not part of that scroll
 * area, so it sits in the exact same spot on every step and never gets
 * pushed off-screen or jumps around when the step (and its content height)
 * changes — the fix for "clicking Next/Back shouldn't move the page".
 *
 * The height budget (`calc(100vh - 300px)`) is tuned against the standalone
 * Explore Tournament Formats page, where the wizard is the page's only real
 * content — clamped between a min (short viewports still get a usable panel)
 * and a max (don't stretch pointlessly tall on big monitors). Embedded in a
 * real tournament's Schedule tab, that page's own header/stat-cards/tabs
 * chrome above this component is unrelated, pre-existing page structure —
 * this only controls the wizard panel's own height, not the whole page.
 *
 * `footer` renders in a `justify-between` row rather than each button
 * carrying its own `ml-*`/`mr-*` margin utility — stream-chat-react's CSS
 * (loaded globally) declares its own `@layer css-reset { button, input, ...
 * { margin: 0 } }`, and since cascade-layer priority is decided by the order
 * layers are FIRST seen across the whole document (not per-file), that
 * layer ends up registered after — and so outranks — Tailwind's own
 * `utilities` layer. Net effect: any margin utility applied directly to a
 * `<button>` is silently cancelled app-wide. `justify-between` isn't a
 * margin property, so it's unaffected. Callers must always pass exactly two
 * children (left, right) — use an empty `<span />` as the left child when
 * there's nothing to show there (see SelectFormatStep's tournament-mode
 * case), since a single child under `justify-between` just sits at the
 * start, not the end.
 */
const WizardFrame = ({ title, subtitle, headerExtra, children, footer }) => {
  const { theme } = useTheme();
  return (
    <div
      className={`tf-animate-in flex flex-col rounded-2xl border ${theme.border} ${theme.card}`}
      style={{ height: 'calc(100vh - 300px)', minHeight: 440, maxHeight: 720 }}
    >
      {(title || headerExtra) && (
        <div className={`flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b ${theme.border} px-5 py-4`}>
          <div>
            {title && <h3 className={`text-lg font-semibold ${theme.text}`}>{title}</h3>}
            {subtitle && <p className={`mt-0.5 text-sm ${theme.textSecondary}`}>{subtitle}</p>}
          </div>
          {headerExtra}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
      {footer && (
        <div className={`flex flex-shrink-0 items-center justify-between gap-3 border-t ${theme.border} px-5 py-4`}>{footer}</div>
      )}
    </div>
  );
};

export default WizardFrame;
